import { useMemo } from 'react';
import { useMonitoringTrailing } from '../../../hooks/useMonitoringTrailing';
import type { OverviewCategory } from '../../../services/dmMonitoringOverview';
import type { MonitoringOverviewHistory } from '../../../services/dmMonitoringOverviewHistory';
import { GranularityToggle, type GranularityOption } from '../../accounts/GranularityToggle';
import { OverviewCard } from '../../billing/overview/OverviewCard';
import { formatDuration } from './overviewTime';
import { CATEGORY_LEGEND, RESOLUTION_LEGEND, RESOLUTION_ORDER } from './overviewLegend';
import type { HistoryView } from './overviewPeriod';
import { HistoryFallback } from './OverviewStates';
import { OverTimeBody, WindowSubtitle } from './OverTimeCardParts';
import {
    BUCKET_OPTIONS,
    bucketWindow,
    buildFixedSeriesChart,
    CATEGORY_FIELD,
    type ChartSeries
} from './overTimeRows';
import { useStoredChoice } from './useStoredChoice';

const CATEGORIES: readonly OverviewCategory[] = ['notification', 'message'];
const NO_MEDIAN = '-';
const SECTION_TITLE = 'text-xs font-semibold text-gray-700';
const STORAGE_KEY_PREFIX = 'monitoring_overview_resolved_';

const CATEGORY_OPTIONS: GranularityOption<OverviewCategory>[] = CATEGORIES.map((category) => ({
    key: category,
    label: CATEGORY_LEGEND[category].label
}));

const RESOLUTION_SERIES: ChartSeries[] = RESOLUTION_ORDER.map((resolution) => ({
    key: resolution,
    ...RESOLUTION_LEGEND[resolution]
}));

function MedianRows({
    medians
}: {
    medians: MonitoringOverviewHistory['medianDashboardResolveSeconds'];
}) {
    return (
        <>
            {CATEGORIES.map((category) => (
                <div key={category} className="flex items-baseline justify-between gap-3 text-sm">
                    <span className="text-gray-700">{CATEGORY_LEGEND[category].label}</span>
                    <span className="text-lg font-semibold tabular-nums text-gray-900">
                        {formatDuration(medians[category]) ?? NO_MEDIAN}
                    </span>
                </div>
            ))}
            <span className="text-[11px] text-gray-500">From DM raising it to our review</span>
        </>
    );
}

// Follows the range bar, unlike the chart's fixed window, so it names its own period.
function MedianReviewTimes({ history }: { history: HistoryView }) {
    return (
        <div className="flex flex-col gap-2.5">
            <span className={SECTION_TITLE}>Median time to review here</span>
            {history.data ? (
                <MedianRows medians={history.data.medianDashboardResolveSeconds} />
            ) : (
                <HistoryFallback failed={history.failed} />
            )}
            <span className="text-[11px] text-gray-400">{history.period.label}</span>
        </div>
    );
}

export function ResolvedCard({ history }: { history: HistoryView }) {
    const [category, setCategory] = useStoredChoice(
        `${STORAGE_KEY_PREFIX}category`,
        CATEGORY_OPTIONS
    );
    const [bucket, setBucket] = useStoredChoice(`${STORAGE_KEY_PREFIX}bucket`, BUCKET_OPTIONS);
    const resolutions = useMonitoringTrailing('resolutions', bucket);
    const shownBucket = resolutions.data?.bucket ?? bucket;
    const buckets = resolutions.data?.[CATEGORY_FIELD[category]];
    const chart = useMemo(
        () => (buckets ? buildFixedSeriesChart(buckets, shownBucket, RESOLUTION_SERIES) : null),
        [buckets, shownBucket]
    );
    const period = bucketWindow(shownBucket);
    const noun = CATEGORY_LEGEND[category].label;
    return (
        <OverviewCard
            title="Resolved"
            subtitle={<WindowSubtitle label={period.label} archive={resolutions.data?.archive} />}
            info="What got closed, and how fast we reviewed it"
            action={
                <div className="flex flex-wrap items-center justify-end gap-2">
                    <GranularityToggle
                        value={category}
                        options={CATEGORY_OPTIONS}
                        onChange={setCategory}
                        ariaLabel="Resolved: notifications or messages"
                    />
                    <GranularityToggle
                        value={bucket}
                        options={BUCKET_OPTIONS}
                        onChange={setBucket}
                        ariaLabel="Resolved: day, week or month"
                    />
                </div>
            }
        >
            <div className="grid grid-cols-1 gap-x-12 gap-y-6 md:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
                <OverTimeBody
                    chart={chart}
                    isLoading={resolutions.isLoading}
                    isSwitching={resolutions.isSwitching}
                    errorMessage={
                        resolutions.isError ? `Couldn't load resolved ${noun.toLowerCase()}` : null
                    }
                    emptyMessage={`Nothing resolved ${period.phrase}`}
                    onRetry={() => void resolutions.retry()}
                />
                <MedianReviewTimes history={history} />
            </div>
        </OverviewCard>
    );
}
